import type { ReactNode } from 'react';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { cn } from '@/lib/utils';
import { Button } from './button';

/**
 * Confirmation dialog. Used for the "editing the past" warning and for the
 * conflict and stale-state messages, all of which share the 16px radius and the
 * two-column 40px button row from the reference.
 */
export const AlertDialog = AlertDialogPrimitive.Root;

export interface AlertDialogBoxProps {
  title: ReactNode;
  children: ReactNode;
  cancelLabel: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel?: () => void;
  /** Some of these dialogs only report; then the left button just closes. */
  confirmVariant?: 'primary' | 'outline';
}

export function AlertDialogBox({
  title,
  children,
  cancelLabel,
  confirmLabel,
  onConfirm,
  onCancel,
  confirmVariant = 'primary',
}: AlertDialogBoxProps) {
  return (
    <AlertDialogPrimitive.Portal>
      <AlertDialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40" />
      <AlertDialogPrimitive.Content
        className={cn(
          'fixed top-1/2 left-1/2 z-50 w-[min(360px,calc(100vw-40px))] -translate-x-1/2 -translate-y-1/2',
          'rounded-alert bg-card p-5 shadow-dialog',
        )}
      >
        <AlertDialogPrimitive.Title className="text-card font-bold text-fg">
          {title}
        </AlertDialogPrimitive.Title>
        <AlertDialogPrimitive.Description asChild>
          <div className="pt-2 text-control leading-relaxed text-fg-tertiary">{children}</div>
        </AlertDialogPrimitive.Description>
        <div className="flex gap-2 pt-4">
          <AlertDialogPrimitive.Cancel asChild>
            <Button variant="outline" size="alert" onClick={onCancel}>
              {cancelLabel}
            </Button>
          </AlertDialogPrimitive.Cancel>
          <AlertDialogPrimitive.Action asChild>
            <Button variant={confirmVariant} size="alert" onClick={onConfirm}>
              {confirmLabel}
            </Button>
          </AlertDialogPrimitive.Action>
        </div>
      </AlertDialogPrimitive.Content>
    </AlertDialogPrimitive.Portal>
  );
}
